import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { usePermissions } from '../contexts/PermissionContext';
import { getApiErrorMessage } from '../utils/apiErrors';
import {
  Search,
  Button,
  Tag,
  InlineLoading,
  InlineNotification,
  TextInput,
  Select,
  SelectItem,
  SkeletonPlaceholder,
  FileUploader,
  ToastNotification
} from '@carbon/react';
import { Add, TrashCan, Edit, Close, ChevronLeft, ChevronRight, Checkmark, Hotel, UserFollow, Connect, Group, Report, ChartLine, Download, OverflowMenuHorizontal, UserMultiple, Filter } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import NativeDatePicker from './NativeDatePicker';
import './ResidentsTable.css';

// Custom Overflow Menu - same design as Resident header
const OverflowMenu = ({ isOpen, onToggle, onClose, menuRef, children, dark = false }) => {
  const menuItemsRef = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const handleKeyDown = (e) => {
    const items = menuItemsRef.current.filter(Boolean);

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        onClose?.();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 + items.length) % items.length);
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(items.length - 1);
        break;
      case 'Tab':
        onClose?.();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && menuItemsRef.current[focusedIndex]) {
      menuItemsRef.current[focusedIndex].focus();
    }
  }, [focusedIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  return (
    <div className={`rph-overflow-menu ${dark ? 'rph-overflow-menu--dark' : ''}`} ref={menuRef}>
      <button
        className="rph-overflow-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Više opcija"
        title="Više opcija"
      >
        <OverflowMenuHorizontal />
      </button>
      {isOpen && (
        <div
          className="rph-overflow-dropdown"
          role="menu"
          aria-label="Dodatne akcije"
          onKeyDown={handleKeyDown}
        >
          {/* `toArray` + `isValidElement` uklanja null/false decu (uslovno
              renderovane stavke). React.Children.map poziva callback i za njih,
              pa bi `cloneElement(null, ...)` pukao. */}
          {React.Children.toArray(children)
            .filter((child) => React.isValidElement(child))
            .map((child, index) =>
              React.cloneElement(child, {
                ref: (el) => (menuItemsRef.current[index] = el),
                tabIndex: focusedIndex === index ? 0 : -1
              })
            )}
        </div>
      )}
    </div>
  );
};

const OverflowMenuItem = React.forwardRef(({ icon: Icon, children, onClick, variant = 'default', tabIndex = -1 }, ref) => (
  <button
    ref={ref}
    className={`rph-overflow-item ${variant === 'danger' ? 'rph-overflow-item--danger' : ''}`}
    onClick={onClick}
    role="menuitem"
    tabIndex={tabIndex}
  >
    {Icon && <span className="rph-overflow-item__icon"><Icon /></span>}
    <span className="rph-overflow-item__label">{children}</span>
  </button>
));
OverflowMenuItem.displayName = 'OverflowMenuItem';

const ResidentsTable = ({ inactiveOnly = false }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';
  // Dodavanje korisnika je dozvoljeno samo uz `residents.create`
  // (administrator ima sve dozvole, pa `can` za njega vraća true).
  const { can } = usePermissions();
  const canCreateResident = can('residents.create');

  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [areas, setAreas] = useState([]);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isFilterExiting, setIsFilterExiting] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [pageFading, setPageFading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepTransition, setStepTransition] = useState('entering');
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomsPage, setRoomsPage] = useState(1);
  const [roomsPageFading, setRoomsPageFading] = useState(false);
  const [roomsPagination, setRoomsPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const roomsPerPage = 4;
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const overflowMenuRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    jmbg: '',
    isWoman: false,
    citizenship: '',
    placeOfBirth: '',
    placeOfResidence: '',
    identificationNumber: '',
    identityCardNumber: '',
    parentName: '',
    primaryLang: '',
    careLevel: 'I',
    roomId: '',
    birthDate: '',
    imgSrc: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });

  // ✅ Synchronous double-submit guard.
  //    `formLoading` is React state, so it only becomes true AFTER a re-render.
  //    A fast double-click (or Enter + click) can therefore fire handleSubmit
  //    twice before the button is disabled, creating two residents.
  //    A ref updates immediately, so the second call is rejected outright.
  const submittingRef = React.useRef(false);

  // Učitaj podatke sa API-ja (server-side paging i pretraga)
  const fetchResidents = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        params.append('pageNumber', String(page));
        params.append('pageSize', String(pageSize));
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        if (areaFilter) {
          params.append('areaId', areaFilter);
        }

        // Use the dedicated inactive endpoint when inactiveOnly is true
        const endpoint = inactiveOnly ? '/api/Resident/inactive' : '/api/Resident';
        const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);
        console.log('API Response:', response);
        console.log('Response data:', response.data);

        // Obrada različitih formata odgovora
        let data = [];

        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data && typeof response.data === 'object') {
          if (Array.isArray(response.data.value)) {
            data = response.data.value;
          } else if (Array.isArray(response.data.$values)) {
            data = response.data.$values;
          } else if (Array.isArray(response.data.data)) {
            data = response.data.data;
          } else if (Array.isArray(response.data.items)) {
            data = response.data.items;
          } else {
            data = [response.data];
          }
        } else if (Array.isArray(response)) {
          data = response;
        }

        console.log('Final data array:', data);
        if (data.length > 0) {
          console.log('Sample resident data:', data[0]);
          console.log('Has imgSrc?', data[0].imgSrc);
        }
        setResidents(data);

        // Pagination metadata
        const pagination = response.data?.pagination;
        if (pagination) {
          setPagination({
            totalCount: pagination.totalCount || 0,
            totalPages: pagination.totalPages || 1,
            hasNextPage: pagination.hasNextPage || false,
            hasPreviousPage: pagination.hasPreviousPage || false
          });
        } else {
          setPagination({
            totalCount: data.length,
            totalPages: Math.ceil(data.length / pageSize) || 1,
            hasNextPage: page < Math.ceil(data.length / pageSize),
            hasPreviousPage: page > 1
          });
        }
      } catch (err) {
        console.error('Greška pri učitavanju korisnika:', err);
        setError('Nije moguće učitati korisnike. Pokušajte ponovo.');
        setResidents([]);
      } finally {
        setLoading(false);
      }
  }, [page, pageSize, searchTerm, areaFilter, inactiveOnly]);

  useEffect(() => {
    fetchResidents();
  }, [fetchResidents]);

  // Učitaj oblasti (za filter)
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const areasRes = await axiosInstance.get('/api/Area');
        const body = areasRes.data;
        let data = [];
        if (Array.isArray(body)) {
          data = body;
        } else if (body && Array.isArray(body.data)) {
          data = body.data;
        } else if (body && Array.isArray(body.value)) {
          data = body.value;
        } else if (body && Array.isArray(body.$values)) {
          data = body.$values;
        } else if (body && Array.isArray(body.items)) {
          data = body.items;
        }
        setAreas(data);
      } catch (err) {
        console.error('Greška pri učitavanju oblasti:', err);
        setAreas([]);
      }
    };

    fetchAreas();
  }, []);

  useEffect(() => {
    if (currentStep === 2 && isSidePanelOpen) {
      setRoomsPage(1);
      fetchAvailableRooms(1);
    }
  }, [currentStep, isSidePanelOpen]);

  // Close overflow menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target)) {
        setIsOverflowMenuOpen(false);
      }
    };

    if (isOverflowMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOverflowMenuOpen]);

  const fetchAvailableRooms = async (pageToFetch = 1) => {
    setRoomsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('pageNumber', String(pageToFetch));
      params.append('pageSize', String(roomsPerPage));
      params.append('onlyAvailable', 'true');
      
      const response = await axiosInstance.get(`/api/Room?${params.toString()}`);
      if (response.data.success) {
        setRooms(response.data.data || []);
        if (response.data.pagination) {
          setRoomsPagination({
            totalCount: response.data.pagination.totalCount || 0,
            totalPages: response.data.pagination.totalPages || 1,
            hasNextPage: response.data.pagination.hasNextPage || false,
            hasPreviousPage: response.data.pagination.hasPreviousPage || false
          });
        }
      }
    } catch (err) {
      console.error('Greška pri učitavanju soba:', err);
      setRooms([]);
    } finally {
      setRoomsLoading(false);
    }
  };

  const handleDeleteResident = async (id) => {
    try {
      await axiosInstance.delete(`/api/Resident/${id}`);
      setResidents(residents.filter(r => r.id !== id));
    } catch (err) {
      console.error('Greška pri brisanju korisnika:', err);
      setError('Nije moguće obrisati korisnika. Pokušajte ponovo.');
    }
  };

  const getCareLevelColor = (level) => {
    const levelMap = {
      'I': 'green',
      'II': 'orange',
      'III': 'red'
    };
    return levelMap[level] || 'gray';
  };

  const getCareLevelLabel = (level) => {
    const levelMap = {
      'I': 'Niska nega',
      'II': 'Srednja nega',
      'III': 'Visoka nega'
    };
    return levelMap[level] || level;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const nextPage = () => {
    if (pagination.hasNextPage) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev + 1);
        setPageFading(false);
      }, 300);
    }
  };

  const prevPage = () => {
    if (pagination.hasPreviousPage) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev - 1);
        setPageFading(false);
      }, 300);
    }
  };

  const handleCloseMobileFilter = () => {
    setIsFilterExiting(true);
    setTimeout(() => {
      setIsMobileFilterOpen(false);
      setIsFilterExiting(false);
    }, 300);
  };

  const clearFilters = () => {
    setAreaFilter('');
    setPage(1);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoomSelect = (room) => {
    const isSelected = selectedRoom?.id === room.id;
    setSelectedRoom(isSelected ? null : room);
    setFormData(prev => ({ ...prev, roomId: isSelected ? '' : room.id }));
  };

  const nextRoomsPage = () => {
    if (roomsPagination.hasNextPage) {
      setRoomsPageFading(true);
      setTimeout(() => {
        const nextPage = roomsPage + 1;
        setRoomsPage(nextPage);
        fetchAvailableRooms(nextPage);
        setRoomsPageFading(false);
      }, 200);
    }
  };

  const prevRoomsPage = () => {
    if (roomsPagination.hasPreviousPage) {
      setRoomsPageFading(true);
      setTimeout(() => {
        const prevPage = roomsPage - 1;
        setRoomsPage(prevPage);
        fetchAvailableRooms(prevPage);
        setRoomsPageFading(false);
      }, 200);
    }
  };

  const goToStep = (step) => {
    setStepTransition('exiting');
    setTimeout(() => {
      setCurrentStep(step);
      setStepTransition('entering');
    }, 150);
  };

  const nextStep = () => {
    if (currentStep < 4) {
      goToStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };

  const handleStepClick = (targetStep) => {
    // Dozvoli vraćanje unazad
    if (targetStep < currentStep) {
      goToStep(targetStep);
      return;
    }

    // Ako pokušava da ide na trenutni korak, ne radi ništa
    if (targetStep === currentStep) {
      return;
    }

    // Proveri validaciju pre prelaska napred
    if (targetStep > currentStep) {
      const validation = validateStep(currentStep);
      if (!validation.isValid) {
        setFormError(validation.message);
        setTimeout(() => setFormError(null), 3000);
        return;
      }
      goToStep(targetStep);
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Basic Info
        if (!formData.firstName || !formData.lastName) {
          return { isValid: false, message: 'Ime i prezime su obavezna polja' };
        }
        if (!formData.jmbg) {
          return { isValid: false, message: 'JMBG je obavezno polje' };
        }
        if (formData.jmbg.length !== 13) {
          return { isValid: false, message: 'JMBG mora imati tačno 13 cifara' };
        }
        if (!/^\d{13}$/.test(formData.jmbg)) {
          return { isValid: false, message: 'JMBG može sadržati samo brojeve' };
        }
        if (!formData.birthDate) {
          return { isValid: false, message: 'Datum rođenja je obavezno polje' };
        }
        break;
      case 1: // Personal Documents
        if (formData.identificationNumber && !/^\d+$/.test(formData.identificationNumber)) {
          return { isValid: false, message: 'Matični broj može sadržati samo brojeve' };
        }
        if (formData.identityCardNumber && !/^\d+$/.test(formData.identityCardNumber)) {
          return { isValid: false, message: 'Broj lične karte može sadržati samo brojeve' };
        }
        break;
      case 2: // Room Assignment
        if (!formData.roomId) {
          return { isValid: false, message: 'Soba je obavezno polje' };
        }
        break;
      case 3: // Care Level
        if (!formData.careLevel) {
          return { isValid: false, message: 'Nivo nege je obavezno polje' };
        }
        break;
      default:
        break;
    }
    return { isValid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Reject a second submit that arrives before React re-renders and
    //    disables the button. Without this, a double-click creates two residents.
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;

    setFormLoading(true);
    setFormError(null);

    try {
      const response = await axiosInstance.post('/api/Resident', {
        ...formData,
        birthDate: formData.birthDate ? new Date(formData.birthDate).toISOString() : new Date().toISOString()
      });

      console.log('Novi korisnik kreiran:', response.data);
      
      // Dodaj novog korisnika u listu
      const newResident = response.data.data || response.data;
      const residentId = newResident?.id;

      // Upload slike nakon kreiranja korisnika
      if (imageFile && residentId) {
        const uploadFormData = new FormData();
        uploadFormData.append('residentId', residentId);
        uploadFormData.append('file', imageFile);

        const uploadResponse = await axiosInstance.post('/api/Resident/upload-image', uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        console.log('Slika uploadovana:', uploadResponse.data);

        if (uploadResponse.data?.success && uploadResponse.data?.data?.url) {
          newResident.imgSrc = uploadResponse.data.data.url;
        }
      }

      // Resetuj formu i zatvori panel
      setFormData({
        firstName: '',
        lastName: '',
        jmbg: '',
        isWoman: false,
        citizenship: '',
        placeOfBirth: '',
        placeOfResidence: '',
        identificationNumber: '',
        identityCardNumber: '',
        parentName: '',
        primaryLang: '',
        careLevel: 'I',
        roomId: '',
        birthDate: '',
        imgSrc: ''
      });
      setImageFile(null);
      setSelectedRoom(null);
      setCurrentStep(0);
      setIsSidePanelOpen(false);

      // Prikaži notifikaciju i osveži stranicu
      setToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: 'Novi korisnik je uspešno kreiran'
      });
      // Osveži listu bez punog reload-a stranice (reload bi na mobilnom
      // ponovo pokrenuo auth proveru i prikazao "Učitavanje..." ekran).
      fetchResidents();
    } catch (err) {
      console.error('Greška pri kreiranju korisnika:', err);

      // 409 = duplikat JMBG-a (ili konflikt). U oba slučaja je poruka backend-a
      // najkorisnija; 400 (npr. `RoomFull`) takođe nosi konkretan razlog.
      if (err.response?.status === 409) {
        setFormError(
          getApiErrorMessage(err, 'Korisnik sa ovim JMBG-om već postoji u vašoj ustanovi.')
        );
      } else {
        setFormError(getApiErrorMessage(err, 'Nije moguće kreirati korisnika. Pokušajte ponovo.'));
      }
    } finally {
      setFormLoading(false);
      // ✅ Release the guard so the user can retry after a failure.
      submittingRef.current = false;
    }
  };

  if (error) {
    return (
      <>
        <div style={{ 
          position: 'fixed', 
          top: '4rem', 
          right: '1rem', 
          zIndex: 9999,
          minWidth: '320px',
          animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <style>
            {`
              @keyframes slideInFromRight {
                from {
                  transform: translateX(calc(100% + 1rem));
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
          </style>
          <InlineNotification
            kind="error"
            title="Greška"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
        <div className="residents-grid-error" />
      </>
    );
  }

  // „Tri tačke" (overflow meni) - prikazuje se u toolbaru, na mestu gde je
  // ranije bila paginacija.
  const overflowMenu = (
    <OverflowMenu
      isOpen={isOverflowMenuOpen}
      onToggle={() => setIsOverflowMenuOpen(!isOverflowMenuOpen)}
      onClose={() => setIsOverflowMenuOpen(false)}
      menuRef={overflowMenuRef}
      dark={isDarkMode}
    >
      <OverflowMenuItem icon={Group} onClick={() => { setIsOverflowMenuOpen(false); navigate('/residents/groups'); }}>
        Grupe
      </OverflowMenuItem>
      <OverflowMenuItem icon={UserMultiple} onClick={() => { setIsOverflowMenuOpen(false); navigate('/residents/inactive'); }}>
        Neaktivni korisnici
      </OverflowMenuItem>
      <OverflowMenuItem icon={Report} onClick={() => { setIsOverflowMenuOpen(false); navigate('/reports'); }}>
        Izveštaji
      </OverflowMenuItem>
      <OverflowMenuItem icon={ChartLine} onClick={() => { setIsOverflowMenuOpen(false); navigate('/analytics'); }}>
        Statistika
      </OverflowMenuItem>
      <OverflowMenuItem icon={Download} onClick={() => {
        setIsOverflowMenuOpen(false);
        const csv = [
          ['Ime', 'Prezime', 'Soba', 'Nivo nege'].join(','),
          ...residents.map(r => [
            `"${r.firstName || ''}"`,
            `"${r.lastName || ''}"`,
            `"${r.room?.name || ''}"`,
            `"${getCareLevelLabel(r.careLevel)}"`
          ].join(','))
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'korisnici.csv';
        link.click();
        URL.revokeObjectURL(url);
      }}>
        Izvezi listu
      </OverflowMenuItem>
    </OverflowMenu>
  );

  // Paginacija (prethodna/sledeća stranica) - prikazuje se ISPOD liste.
  const renderPagination = () => (
    <div className="residents-pagination">
      <Button
        kind="ghost"
        size="md"
        renderIcon={ChevronLeft}
        onClick={prevPage}
        hasIconOnly
        disabled={!pagination.hasPreviousPage}
        iconDescription="Prethodna stranica"
      />
      <span className="residents-pagination-label">
        {page}/{pagination.totalPages}
      </span>
      <Button
        kind="ghost"
        size="md"
        renderIcon={ChevronRight}
        onClick={nextPage}
        hasIconOnly
        disabled={!pagination.hasNextPage}
        iconDescription="Sledeća stranica"
      />
    </div>
  );

  return (
    <div className="residents-grid-container">
      {/* Toolbar */}
      <div className="residents-toolbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
          <Search
            size="lg"
            placeholder="Pretražite korisnike..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            onClear={() => {
              setSearchTerm('');
              setPage(1);
            }}
            labelText=""
            closeButtonLabelText="Obriši pretragu"
            style={{ flex: 1 }}
          />
          {/* „Tri tačke" (overflow meni) - na mestu gde je ranije bila paginacija */}
          {overflowMenu}
        </div>
        <div className="residents-toolbar-actions">
          {canCreateResident && (
            <Button
              kind="primary"
              renderIcon={Add}
              onClick={() => {
                setIsSidePanelOpen(true);
                setCurrentStep(0);
              }}
              className="add-resident-button"
            >
              Dodaj korisnika
            </Button>
          )}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div
          className="residents-grid residents-grid--loading"
          style={{
            marginBottom: '2rem'
          }}
        >
          {[...Array(4)].map((_, index) => (
            <SkeletonPlaceholder
              key={index}
              style={{
                width: '100%',
                height: '210px',
                borderRadius: '8px'
              }}
            />
          ))}
        </div>
      ) : residents.length === 0 ? (
        <div className="residents-empty">
          <div className="empty-icon">📋</div>
          <p className="empty-title">Nema korisnika</p>
          <p className="empty-subtitle">
            {searchTerm ? 'Pokušajte sa drugom pretragom' : 'Dodajte prvog korisnika'}
          </p>
        </div>
      ) : (
        <div 
          className="residents-grid"
          style={{
            opacity: pageFading ? 0 : 1,
            transition: 'opacity 0.3s ease',
            animation: 'fadeIn 0.4s ease-in'
          }}
        >
          {residents.map((resident) => {
            const initials = getInitials(resident.firstName, resident.lastName);
            const hasImage = resident.imgSrc && resident.imgSrc !== '';

            return (
              <div 
                key={resident.id} 
                className="resident-card"
                onClick={() => navigate(`/residents/${resident.id}`)}
              >
                {/* Avatar */}
                <div className="resident-card-avatar">
                  {hasImage ? (
                    <>
                      <img 
                        src={resident.imgSrc} 
                        alt={`${resident.firstName} ${resident.lastName}`}
                        className="resident-card-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="resident-card-initials"
                        style={{ display: 'none' }}
                      >
                        {initials || '?'}
                      </div>
                    </>
                  ) : (
                    <div className="resident-card-initials">
                      {initials || '?'}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="resident-card-content">
                  <h3 className="resident-card-name">
                    {resident.firstName} {resident.lastName}
                  </h3>
                  
                  <div className="resident-card-details">
                    <div className="resident-card-detail">
                      <span className="detail-label">Soba:</span>
                      <span className="detail-value">{resident.room?.name || '-'}</span>
                    </div>
                    
                    <div className="resident-card-detail">
                      <span className="detail-label">Nivo nege:</span>
                      <Tag
                        type={getCareLevelColor(resident.careLevel)}
                        size="sm"
                      >
                        {getCareLevelLabel(resident.careLevel)}
                      </Tag>
                    </div>
                    
                    {resident.birthDate && (
                      <div className="resident-card-detail">
                        <span className="detail-label">Datum rođenja:</span>
                        <span className="detail-value">
                          {new Date(resident.birthDate).toLocaleDateString('sr-Latn-RS')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      )}

      {/* Paginacija - ispod liste */}
      {!loading && residents.length > 0 && pagination.totalPages > 1 && renderPagination()}

      {/* Right Side Panel */}
      <div className={`resident-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header" style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {currentStep === 0 && 'Osnovne informacije'}
            {currentStep === 1 && 'Lična dokumenta'}
            {currentStep === 2 && 'Smeštaj'}
            {currentStep === 3 && 'Nivo nege'}
            {currentStep === 4 && 'Potvrda podataka'}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={() => {
              setIsSidePanelOpen(false);
              setCurrentStep(0);
              setSelectedRoom(null);
              setFormData({
                firstName: '',
                lastName: '',
                jmbg: '',
                isWoman: false,
                citizenship: '',
                placeOfBirth: '',
                placeOfResidence: '',
                identificationNumber: '',
                identityCardNumber: '',
                parentName: '',
                primaryLang: '',
                careLevel: 'I',
                roomId: '',
                birthDate: '',
                imgSrc: ''
              });
            }}
          />
        </div>

        {/* Progress Indicator */}
        <div className="resident-progress-indicator" style={{ 
          padding: '1rem 1.5rem 0.75rem', 
          borderBottom: '1px solid var(--cds-border-subtle)',
          backgroundColor: 'var(--cds-layer-01)'
        }}>
          <div className="custom-progress-bar">
            <div className="progress-steps">
              {[0, 1, 2, 3, 4].map((step) => (
                <React.Fragment key={step}>
                  <div 
                    className={`progress-step ${
                      currentStep >= step ? 'completed' : ''
                    } ${currentStep === step ? 'active' : ''}`}
                    onClick={() => handleStepClick(step)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="step-circle">
                      {currentStep > step ? <span style={{ fontSize: '16px' }}>✓</span> : step + 1}
                    </div>
                  </div>
                  {step < 4 && (
                    <div className={`progress-line ${currentStep > step ? 'completed' : ''}`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {formError && (
          <div style={{ 
            position: 'fixed', 
            top: '4rem', 
            right: '1rem', 
            zIndex: 9999,
            minWidth: '320px',
            animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <style>
              {`
                @keyframes slideInFromRight {
                  from {
                    transform: translateX(calc(100% + 1rem));
                    opacity: 0;
                  }
                  to {
                    transform: translateX(0);
                    opacity: 1;
                  }
                }
              `}
            </style>
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={formError}
              onCloseButtonClick={() => setFormError(null)}
              hideCloseButton={false}
              lowContrast
            />
          </div>
        )}
        <div className="side-panel-content">

          <form onSubmit={handleSubmit} className="resident-form">
            {/* Step 0: Basic Info */}
            {currentStep === 0 && (
              <div className={`step-content ${stepTransition}`}>
                <TextInput
                  id="firstName"
                  labelText="Ime *"
                  placeholder="Unesite ime"
                  value={formData.firstName}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  disabled={formLoading}
                />

                <TextInput
                  id="lastName"
                  labelText="Prezime *"
                  placeholder="Unesite prezime"
                  value={formData.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  disabled={formLoading}
                />

                <TextInput
                  id="jmbg"
                  labelText="JMBG *"
                  placeholder="1234567890123"
                  value={formData.jmbg}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 13) {
                      handleFormChange('jmbg', value);
                    }
                  }}
                  disabled={formLoading}
                  maxLength={13}
                  helperText="Unesite 13 cifara"
                />

                <NativeDatePicker
                  id="birthDate"
                  labelText="Datum rođenja *"
                  value={formData.birthDate}
                  onChange={(dates) => handleFormChange('birthDate', dates[0])}
                  disabled={formLoading}
                />

                <Select
                  id="isWoman"
                  labelText="Pol *"
                  value={formData.isWoman.toString()}
                  onChange={(e) => handleFormChange('isWoman', e.target.value === 'true')}
                  disabled={formLoading}
                >
                  <SelectItem value="false" text="Muški" />
                  <SelectItem value="true" text="Ženski" />
                </Select>

                <div style={{ marginTop: '0.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: isDarkMode ? '#f4f4f4' : 'inherit'
                  }}>
                    Slika korisnika (opciono)
                  </label>
                  <FileUploader
                    labelTitle=""
                    labelDescription="Samo JPG, PNG, WEBP, GIF, BMP (maks 5MB)"
                    buttonLabel="Izaberi sliku"
                    filenameStatus="edit"
                    accept={['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp']}
                    multiple={false}
                    disabled={formLoading}
                    onChange={(e) => setImageFile(e.target.files[0])}
                  />
                </div>
              </div>
            )}

            {/* Step 1: Personal Documents */}
            {currentStep === 1 && (
              <div className={`step-content ${stepTransition}`}>
                <TextInput
                  id="citizenship"
                  labelText="Državljanstvo"
                  placeholder="Srbija"
                  value={formData.citizenship}
                  onChange={(e) => handleFormChange('citizenship', e.target.value)}
                  disabled={formLoading}
                />

                <TextInput
                  id="placeOfBirth"
                  labelText="Mesto rođenja"
                  placeholder="Beograd"
                  value={formData.placeOfBirth}
                  onChange={(e) => handleFormChange('placeOfBirth', e.target.value)}
                  disabled={formLoading}
                />

                <TextInput
                  id="placeOfResidence"
                  labelText="Mesto prebivališta"
                  placeholder="Beograd, Srbija"
                  value={formData.placeOfResidence}
                  onChange={(e) => handleFormChange('placeOfResidence', e.target.value)}
                  disabled={formLoading}
                />

                <TextInput
                  id="identificationNumber"
                  labelText="Matični broj"
                  placeholder="Matični broj"
                  value={formData.identificationNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    handleFormChange('identificationNumber', value);
                  }}
                  disabled={formLoading}
                  helperText="Samo brojevi"
                />

                <TextInput
                  id="identityCardNumber"
                  labelText="Broj lične karte"
                  placeholder="Broj lične karte"
                  value={formData.identityCardNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    handleFormChange('identityCardNumber', value);
                  }}
                  disabled={formLoading}
                  helperText="Samo brojevi"
                />

                <TextInput
                  id="parentName"
                  labelText="Ime roditelja"
                  placeholder="Ime oca/majke"
                  value={formData.parentName}
                  onChange={(e) => handleFormChange('parentName', e.target.value)}
                  disabled={formLoading}
                />

                <TextInput
                  id="primaryLang"
                  labelText="Primarni jezik"
                  placeholder="Srpski"
                  value={formData.primaryLang}
                  onChange={(e) => handleFormChange('primaryLang', e.target.value)}
                  disabled={formLoading}
                />
              </div>
            )}

            {/* Step 2: Room Assignment */}
            {currentStep === 2 && (
              <div className={`step-content ${stepTransition}`}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--cds-text-primary)', margin: 0 }}>
                    Dostupne sobe *
                  </label>
                  {roomsPagination.totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={ChevronLeft}
                        onClick={prevRoomsPage}
                        hasIconOnly
                        disabled={!roomsPagination.hasPreviousPage}
                        iconDescription="Prethodne sobe"
                      />
                      <span style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', minWidth: '40px', textAlign: 'center' }}>
                        {roomsPage}/{roomsPagination.totalPages}
                      </span>
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={ChevronRight}
                        onClick={nextRoomsPage}
                        hasIconOnly
                        disabled={!roomsPagination.hasNextPage}
                        iconDescription="Sledeće sobe"
                      />
                    </div>
                  )}
                </div>
                {roomsLoading ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                    {[...Array(4)].map((_, idx) => (
                      <div key={idx} style={{ height: '100px' }}>
                        <SkeletonPlaceholder style={{ width: '100%', height: '100%' }} />
                      </div>
                    ))}
                  </div>
                ) : rooms.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem', 
                    color: 'var(--cds-text-secondary)',
                    background: 'var(--cds-layer-02)',
                    borderRadius: '8px'
                  }}>
                    Nema dostupnih soba
                  </div>
                ) : (
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    opacity: roomsPageFading ? 0 : 1,
                    transition: 'opacity 0.2s ease'
                  }}>
                    {rooms
                      .map((room) => {
                      const isSelected = selectedRoom?.id === room.id;
                      const availableBeds = room.bedsNumber - room.takenBeds;
                      return (
                        <div
                          key={room.id}
                          onClick={() => handleRoomSelect(room)}
                          style={{
                            padding: '0.75rem',
                            border: isSelected ? '2px solid #9fb746' : '1px solid var(--cds-border-subtle)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            background: isSelected ? 'rgba(159, 183, 70, 0.1)' : 'var(--cds-layer-01)',
                            transition: 'all 0.2s ease',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = '#9fb746';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = 'var(--cds-border-subtle)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          {isSelected && (
                            <div style={{
                              position: 'absolute',
                              top: '0.5rem',
                              right: '0.5rem',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              background: '#9fb746',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              ✓
                            </div>
                          )}
                          <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Hotel size={20} style={{ color: '#9fb746' }} />
                            Soba {room.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginBottom: '0.25rem' }}>
                            {room.areaName}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)' }}>
                            Slobodno: {availableBeds}/{room.bedsNumber} kreveta
                          </div>
                          <Tag
                            type="blue"
                            size="sm"
                            style={{ marginTop: '0.5rem' }}
                          >
                            {room.careLevel}
                          </Tag>
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedRoom && (
                  <div style={{ 
                    padding: '0.75rem', 
                    background: 'var(--cds-layer-02)', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    marginBottom: '1rem'
                  }}>
                    <strong>Izabrana soba:</strong> {selectedRoom.name} ({selectedRoom.areaName})
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Care Level */}
            {currentStep === 3 && (
              <div className={`step-content ${stepTransition}`}>
                <Select
                  id="careLevel"
                  labelText="Nivo nege *"
                  value={formData.careLevel}
                  onChange={(e) => handleFormChange('careLevel', e.target.value)}
                  disabled={formLoading}
                >
                  <SelectItem value="I" text="Niska nega (I)" />
                  <SelectItem value="II" text="Srednja nega (II)" />
                  <SelectItem value="III" text="Visoka nega (III)" />
                </Select>
                <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                  Nivo nege određuje intenzitet medicinske i svakodnevne nege.
                </p>
              </div>
            )}

            {/* Step 4: Confirm */}
            {currentStep === 4 && (
              <div className={`step-content ${stepTransition}`}>
                <div style={{ 
                  background: 'var(--cds-layer-02)', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 600 }}>Osnovne informacije</h4>
                  <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <div><strong>Ime i prezime:</strong> {formData.firstName} {formData.lastName}</div>
                    <div><strong>JMBG:</strong> {formData.jmbg || '-'}</div>
                    <div><strong>Pol:</strong> {formData.isWoman ? 'Ženski' : 'Muški'}</div>
                    <div><strong>Datum rođenja:</strong> {formData.birthDate ? new Date(formData.birthDate).toLocaleDateString('sr-Latn-RS') : '-'}</div>
                  </div>
                </div>

                {(formData.citizenship || formData.placeOfBirth || formData.identityCardNumber) && (
                  <div style={{ 
                    background: 'var(--cds-layer-02)', 
                    padding: '1rem', 
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 600 }}>Dokumenta</h4>
                    <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                      {formData.citizenship && <div><strong>Državljanstvo:</strong> {formData.citizenship}</div>}
                      {formData.placeOfBirth && <div><strong>Mesto rođenja:</strong> {formData.placeOfBirth}</div>}
                      {formData.placeOfResidence && <div><strong>Prebivalište:</strong> {formData.placeOfResidence}</div>}
                      {formData.identityCardNumber && <div><strong>Br. lične karte:</strong> {formData.identityCardNumber}</div>}
                      {formData.parentName && <div><strong>Ime roditelja:</strong> {formData.parentName}</div>}
                      {formData.primaryLang && <div><strong>Jezik:</strong> {formData.primaryLang}</div>}
                    </div>
                  </div>
                )}

                <div style={{ 
                  background: 'var(--cds-layer-02)', 
                  padding: '1rem', 
                  borderRadius: '8px'
                }}>
                  <h4 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 600 }}>Smeštaj i nega</h4>
                  <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                    <div><strong>Soba:</strong> {selectedRoom ? `${selectedRoom.name} (${selectedRoom.areaName})` : formData.roomId}</div>
                    <div><strong>Nivo nege:</strong> {getCareLevelLabel(formData.careLevel)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="form-actions" style={{ marginTop: '1.5rem' }}>
              {currentStep > 0 && (
                <Button
                  kind="secondary"
                  onClick={prevStep}
                  disabled={formLoading}
                >
                  Nazad
                </Button>
              )}
              {currentStep < 4 ? (
                <Button
                  kind="primary"
                  onClick={() => {
                    const validation = validateStep(currentStep);
                    if (validation.isValid) {
                      nextStep();
                    } else {
                      setFormError(validation.message);
                      setTimeout(() => setFormError(null), 3000);
                    }
                  }}
                  disabled={formLoading}
                  className="add-resident-button"
                >
                  Dalje
                </Button>
              ) : (
                <Button
                  kind="primary"
                  type="submit"
                  disabled={formLoading}
                  className="add-resident-button"
                >
                  {formLoading ? 'Čuvanje...' : 'Sačuvaj'}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Overlay */}
      {isSidePanelOpen && (
        <div 
          className="side-panel-overlay" 
          onClick={() => setIsSidePanelOpen(false)}
        />
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className="toast-container">
          <ToastNotification
            kind={toast.kind}
            title={toast.title}
            subtitle={toast.subtitle}
            timeout={0}
            onClose={() => setToast(prev => ({ ...prev, show: false }))}
            lowContrast
          />
        </div>
      )}

      {/* Mobile Filter Button */}
      {!isSidePanelOpen && ReactDOM.createPortal(
        <button
          className="floating-button resident-mobile-filter-btn"
          onClick={() => setIsMobileFilterOpen(true)}
          style={{
            position: 'fixed',
            bottom: '2rem',
            left: '2rem',
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9998
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#8da63d';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#9fb746';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
          }}
        >
          <Filter
            size={24}
            style={{
              flexShrink: 0,
              minWidth: '24px',
              minHeight: '24px',
              fill: '#ffffff'
            }}
            aria-label="Filteri"
            color="#ffffff"
          />
          <span style={{
            color: '#ffffff',
            fontWeight: '500',
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            Filteri
          </span>
        </button>,
        document.body
      )}

      {/* Floating Action Button - Dodaj korisnika (mobile only) */}
      {canCreateResident && !isSidePanelOpen && ReactDOM.createPortal(
        <button
          className="floating-button resident-fab"
          onClick={() => {
            setIsSidePanelOpen(true);
            setCurrentStep(0);
          }}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9999
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#8da63d';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#9fb746';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
          }}
        >
          <Add
            size={24}
            style={{
              flexShrink: 0,
              minWidth: '24px',
              minHeight: '24px',
              fill: '#ffffff'
            }}
            aria-label="Dodaj korisnika"
            color="#ffffff"
          />
          <span style={{
            color: 'white',
            fontWeight: '500',
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            Dodaj korisnika
          </span>
        </button>,
        document.body
      )}

      {/* Mobile Filter Modal */}
      {isMobileFilterOpen && ReactDOM.createPortal(
        <div
          className={isFilterExiting ? 'filter-modal-exiting' : ''}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDarkMode ? 'rgba(22, 22, 22, 0.85)' : 'rgba(244, 244, 244, 0.85)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-end',
            animation: isFilterExiting ? 'fadeOut 0.2s ease-out' : 'fadeIn 0.2s ease-out'
          }}
          onClick={handleCloseMobileFilter}
        >
          <div
            className="resident-mobile-filter-panel"
            data-carbon-theme={theme}
            style={{
              width: '100%',
              backgroundColor: isDarkMode ? '#262626' : '#ffffff',
              borderRadius: '16px 16px 0 0',
              padding: '1.5rem',
              animation: isFilterExiting ? 'slideDownOut 0.3s ease-out' : 'slideUpIn 0.3s ease-out',
              maxHeight: '70vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500', color: isDarkMode ? '#ffffff' : '#161616' }}>Filteri</h3>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                className="resident-mobile-filter-close-btn"
                renderIcon={Close}
                iconDescription="Zatvori"
                onClick={handleCloseMobileFilter}
                style={{ color: isDarkMode ? '#ffffff' : '#161616' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Select
                id="mobile-area-filter"
                labelText="Oblast"
                value={areaFilter}
                onChange={(e) => {
                  setAreaFilter(e.target.value);
                  setPage(1);
                }}
                size="md"
              >
                <SelectItem value="" text="Sve oblasti" />
                {areas.map((area) => (
                  <SelectItem key={area.id} value={area.id} text={area.name} />
                ))}
              </Select>

              <Button
                kind="primary"
                size="md"
                style={{ width: '100%', marginTop: '1rem' }}
                onClick={handleCloseMobileFilter}
              >
                Primeni filtere
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ResidentsTable;
